import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

// `withCredentials` joint le cookie de session HttpOnly à chaque appel.
// C'est le SEUL mécanisme d'authentification du navigateur : aucun jeton n'est
// lu ni écrit en JavaScript (ni localStorage, ni sessionStorage, ni URL).
// L'en-tête `Authorization: Bearer` reste accepté par l'API pour les clients
// non-navigateur (tests, scripts serveur), qui n'ont pas de gestion de cookies.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// --- Rafraîchissement transparent de la session ---------------------------
// Le jeton d'accès ne vit que 2 heures. Sans ce mécanisme, l'utilisateur serait
// déconnecté en pleine saisie. Sur le premier 401, on tente une fois de
// prolonger la session via le cookie de rafraîchissement, puis on rejoue la
// requête d'origine. L'utilisateur ne voit rien.
//
// `_dejaRejoue` empêche une boucle : si le rafraîchissement échoue lui aussi,
// le 401 remonte normalement et l'application renvoie vers la page de connexion.
let rafraichissementEnCours = null;

api.interceptors.response.use(
  (reponse) => reponse,
  async (erreur) => {
    const requete = erreur.config;
    const estUn401 = erreur.response?.status === 401;
    const estUneRouteDAuth =
      requete?.url?.includes("/auth/refresh") ||
      requete?.url?.includes("/auth/login") ||
      requete?.url?.includes("/auth/logout");

    if (!estUn401 || !requete || requete._dejaRejoue || estUneRouteDAuth) {
      return Promise.reject(erreur);
    }
    requete._dejaRejoue = true;

    try {
      // Un seul appel de rafraîchissement même si plusieurs requêtes échouent
      // en même temps : la rotation invaliderait les appels concurrents.
      if (!rafraichissementEnCours) {
        rafraichissementEnCours = api
          .post("/auth/refresh")
          .finally(() => {
            rafraichissementEnCours = null;
          });
      }
      await rafraichissementEnCours;
      return api(requete);
    } catch (_e) {
      return Promise.reject(erreur);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function getAxiosErrorMessage(e) {
  return formatApiErrorDetail(e?.response?.data?.detail) || e.message;
}
